import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.DEBUG ) );

import { DateTime } from "luxon";
import { articles, syncState } from "@/plugins/LocalForagePlugin";
import { articlesSyncKey } from "@/types/products/product-definitions";
import type {
    Article,
    ArticleMetaData,
    Author,
    Category,
    ContentfulArticle,
    HasMetaData,
    IndustrySector,
    ContentfulSector,
    Product,
    SearchableTicker,
    Tag
} from "@tmi/types/entities";
import type { Articles } from "@tmi/types/api/content-models";
import { goFetch } from "@/utils/network-utils";
import { batchSetItems, batchRemoveItems } from "@/utils/localforage-batch";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import { CompositionContext } from "@/services/data/sync-supervisor";

export class ArticlesSyncCoordinator {
    readonly #compositionContext: CompositionContext;

    constructor( ctx: CompositionContext ) {
        this.#compositionContext = ctx;
    }

    public async patchRepository( authToken: string ): Promise< number > {
        let itemsUpdated = 0;

        try {
            const syncThresholdInMillis = await syncState.getItem< number >( articlesSyncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
                logger.debug( `[ articles-sync-coordinator ] load all available article meta-data...` );
                const t0: number = performance.now();

                // Retrieve & store the latest meta-data for articles (all - both public and subscription-based)
                const allMetadata = await this.refreshMetaDataFromServer( authToken );

                if ( 0 === allMetadata.length ) {
                    const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                    await syncState.setItem( articlesSyncKey, nextSyncThresholdInMillis );

                    return itemsUpdated;
                }

                // Set throttle after successful fetch
                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.setItem( articlesSyncKey, nextSyncThresholdInMillis );

                const t1: number = performance.now();
                logger.info( `[ articles-sync-coordinator ] Retrieved & loaded ${ allMetadata.length } article meta records in ${ t1 - t0 } ms` );

                const allMetaDataByIdMap = new Map< string, ArticleMetaData >(
                    allMetadata.map( ( meta: HasMetaData ) => [ meta.contentfulId, meta ] )
                );

                // Manage all cached subscription-based articles
                const t2: number = performance.now();

                if ( await articles.length() > 0 ) {
                    const keysToRemove: string[] = [];

                    // Iterate over all elements within IDB
                    await articles.iterate( ( content: Article, key: string ) => {
                        const meta: ArticleMetaData | undefined = allMetaDataByIdMap.get( key );
                        if ( typeof meta !== 'undefined' ) {
                            if ( 0 === meta.updatedDate?.localeCompare( content.updatedDate ?? '' ) ) {
                                allMetaDataByIdMap.delete( key );
                            } else {
                                keysToRemove.push( key );
                            }
                        } else {
                            keysToRemove.push( key );
                        }
                        return;
                    } );

                    await batchRemoveItems( articles, keysToRemove );
                }

                const t3: number = performance.now();
                logger.info( `[ articles-sync-coordinator ] Aggregated missing subscription articles in ${ t3 - t2 } ms` );

                // Transform metadata map into an array of content ids
                const missingIds = Array.from( allMetaDataByIdMap.keys() );
                // If any content is missing...
                if ( missingIds.length > 0 ) {
                    // Fetch it from the server
                    itemsUpdated = await this.refreshContentFromServer( authToken, missingIds );
                }

                const t4: number = performance.now();
                logger.info( `[ articles-sync-coordinator ] Processing all missing articles took ${ t4 - t0 } ms` );
            }
        } catch ( err: any ) {
            logger.error( '[ articles-sync-coordinator ] Sync failed:', err );
        }

        return itemsUpdated;
    }

    private async refreshMetaDataFromServer( authToken: string ): Promise< ArticleMetaData[] > {
        let page: number = 0;
        const pageSize: number = 5000;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        const metadata: ArticleMetaData[] | undefined = [];
        let metaPayload: ContentfulArticle[] | undefined = [];

        do {
            const response =
                await goFetch< Articles, PersistedQueryPayload >( {
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '27f9b6993a8eb710c390171b82547d1d',
                        variables: {
                            skip: page,
                            limit: pageSize,
                            contentFilter: {
                                fromPublishedDateTimeUTC: '2008-01-01'
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Article metadata fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping article metadata pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            metaPayload = response?.data?.articlesCatalog?.articles;
            if ( metaPayload ) {
                metaPayload.forEach( ( a: ContentfulArticle ) => {
                    metadata.push( {
                        contentfulId: a.id,
                        slug: a.slug,
                        updatedDate: a.publish_at
                    } );
                } );
            }
            // update the page #
            page++;
        } while ( (metaPayload?.length ?? 0) >= pageSize );

        return metadata;
    }

    private async refreshContentFromServer( authToken: string, missingContentIds: string[] ): Promise< number > {
        // Timing - testing to see how expensive each method is
        const t0: number = performance.now();
        let itemsUpdated = 0;

        const pageSize: number = 350;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        for ( let offset = 0; offset < missingContentIds.length; offset += pageSize ) {
            const idsForPage = missingContentIds.slice( offset, offset + pageSize );

            const response =
                await goFetch< Articles, PersistedQueryPayload >( {
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '1f2736d6d67c809d2304318cc1c20a74',
                        variables: {
                            skip: 0,
                            limit: pageSize,
                            contentFilter: {
                                ids: idsForPage
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Article content fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping article content pagination');
                    break;
                }

                continue;
            }

            consecutiveFailures = 0;

            const contentPayload = response?.data?.articlesCatalog?.articles;
            if ( null != contentPayload ) {
                const allArticles: Article[] = [];
                contentPayload.forEach( ( a: ContentfulArticle ) => {
                    const article: Article | undefined = composeArticle( a, this.#compositionContext );
                    if ( typeof article !== 'undefined' ) {
                        allArticles.push( article );
                    }
                } );

                // Store the items recently retrieved
                await articles.ready();
                try {
                    await batchSetItems( articles, allArticles.map( a => [ a.contentfulId, a ] as const ) );
                } catch ( err: any ) {
                    logger.error( '[ articles-sync-coordinator ] Sync failed:', err );
                }

                const t1: number = performance.now();
                logger.info( `[ articles-sync-coordinator ] Processing ${ allArticles.length } articles took ${ t1 - t0 } ms` );

                itemsUpdated += allArticles.length;
            }
        }

        return itemsUpdated;
    }
}

export function composeArticle( article: ContentfulArticle, ctx: CompositionContext ): Article | undefined {
    try {
        const author: Author | undefined = ctx.getAuthorById( article.author_id );

        const adProduct: Product | undefined = article.ad_product_id
            ? ctx.getProductById( article.ad_product_id ) : undefined;

        const categories: Category[] = [];
        article.assoc_category_ids?.forEach( ( id: string ) => {
            const category = ctx.getCategoryById( id );
            if ( category ) categories.push( category );
        } );

        const industrySectors: IndustrySector[] = [];
        article.assoc_ind_sector_ids?.forEach( ( id: string ) => {
            const industrySector = ctx.getIndustrySectorById( id );
            if ( industrySector ) industrySectors.push( industrySector );
        } );

        const products: Product[] = [];
        article.assoc_product_ids?.forEach( ( id: string ) => {
            const product = ctx.getProductById( id );
            if ( product ) {
                products.push( product );
            } else {
                logger.warn( `The given product id '${ id }' was not found to match a known Product content item.` );
            }
        } );

        const sectors: ContentfulSector[] = [];
        article.assoc_sector_ids?.forEach( ( id: string ): void => {
            const sector = ctx.getSectorById( id );
            if ( sector ) sectors.push( sector );
        } );

        const tags: Tag[] = [];
        article.assoc_tag_ids?.forEach( ( id: string ): void => {
            const tag = ctx.getTagById( id );
            if ( tag ) tags.push( tag );
        } );

        const tickers: SearchableTicker[] = [];
        article.assoc_ticker_ids?.forEach( ( id: string ): void => {
            const ticker = ctx.getTickerById( id );
            if ( ticker ) tickers.push( ticker );
        } );

        return ( {
            contentfulId: article.id,
            publishedDate: article.publish_dt,

            updatedDate: article.publish_at,

            slug: article.slug ?? undefined,
            title: article.title ?? undefined,
            text: article.body ?? undefined,
            body2: article.body2 ?? undefined,
            preview: article.preview ?? undefined,
            thumbnail: article.thumbnail_uri ?? undefined,

            adProduct: adProduct,
            editorialAd: article.editorial_ad ?? undefined,

            videoLink: null === article.video_id ? undefined : `https://vimeo.com/${ article.video_id }`,

            author: author,
            categories: categories,
            industrySectors: industrySectors,
            products: products,
            sectors: sectors,
            tags: tags,
            tickers: tickers
        } );
    } catch ( e: any ) {
        logger.error( `[ articles-sync-coordinator ] Failed to compose article '${ article.id }':`, e );
        return undefined;
    }
}